import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Check, ArrowRight, Sparkles, GraduationCap, Bot, ChevronLeft, Headphones, BookOpen, Mic, Pencil, Brain, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { T, PrimaryBtn, HudFrame, AIPropsBackground, AuthBrand, useClickSound } from "@/pages/AuthPreviewShared";
import EasyPaisaCheckout from "@/components/EasyPaisaCheckout";

/**
 * EnglishTest.UK Pricing — 3-tier
 *  - Free Assessment (lead gen): name+email+country+phone+target → short Speaking mock
 *  - Self Prep AI: $14.99 / month
 *  - Hybrid (Live + AI Mocks): $54 / complete 8-week course
 *
 * Subscribe buttons currently log lead intent to /api/leads/plan-interest.
 * Stripe checkout will be wired in a follow-up round.
 */
const PLANS = [
  {
    id: "free",
    badge: "FREE FOREVER",
    name: "Free Assessment",
    price: "$0",
    cycle: "no card needed",
    blurb: "Take a 5-minute AI Speaking mock and get an instant predicted band.",
    Icon: Sparkles,
    cta: "Start Free Assessment",
    ctaTo: "/design-preview/free-assessment",
    features: [
      "5-minute AI Speaking mock",
      "Instant predicted band score",
      "Personalised improvement tips",
      "Email PDF report",
      "Limited to 1 assessment / 24h",
    ],
    accent: T.text,
    featured: false,
  },
  {
    id: "self_prep_ai",
    badge: "MOST POPULAR",
    name: "Self Prep AI",
    price: "$14.99",
    cycle: "per month · cancel anytime",
    blurb: "Unlimited AI examiners, full mock suite, predicted band tracking.",
    Icon: Bot,
    cta: "Subscribe to Self Prep",
    features: [
      "Unlimited AI Speaking, Writing, Listening, Reading mocks",
      "Full IELTS · PTE · Oxford ELLT · Duolingo simulators",
      "Paper-based + Computer-based realism",
      "AI Coach chat 24/7",
      "Weakness heatmaps & study plans",
      "Cancel anytime",
    ],
    accent: T.primary,
    featured: true,
  },
  {
    id: "hybrid_live",
    badge: "BEST VALUE",
    name: "Hybrid Live + AI",
    price: "$54",
    cycle: "one-time · 8-week complete course",
    blurb: "Everything in Self Prep + live online classes with certified tutors.",
    Icon: GraduationCap,
    cta: "Enrol in Hybrid",
    features: [
      "Everything in Self Prep AI",
      "Live online classes · 4 sessions / week",
      "Certified IELTS · PTE · Oxford · Duolingo tutors",
      "Small group (≤ 8 students)",
      "Personal mentor & assignment grading",
      "Money-back guarantee if no band improvement",
    ],
    accent: T.secondary,
    featured: false,
  },
];

export default function PricingPreview() {
  const play = useClickSound();
  const [loading, setLoading] = useState(null);
  const [easypaisaOpen, setEasypaisaOpen] = useState(null); // { id, name } when open
  const { user } = useAuth();
  const nav = useNavigate();
  const isPK = (user?.country_code || "").toUpperCase() === "PK";

  const onSubscribe = async (plan) => {
    if (plan.ctaTo) {
      window.location.href = plan.ctaTo;
      return;
    }
    if (!user) {
      // Send unauth user to signup with a return target on the pricing page
      try { await api.post("/leads/plan-interest", { plan_id: plan.id, plan_name: plan.name }); } catch (e) { /* optional */ }
      toast.message("Create an account to subscribe — we kept your selection.");
      nav(`/signup?next=/pricing&plan=${plan.id}`);
      return;
    }
    setLoading(plan.id);
    try {
      const res = await api.post("/billing/create-checkout-session", {
        plan_id: plan.id,
        origin_url: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      throw new Error("No checkout URL returned");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Could not start checkout. Please try again.";
      toast.error(msg);
    } finally { setLoading(null); }
  };

  const onEasypaisa = (plan) => {
    if (!user) {
      toast.message("Create an account first — we kept your selection.");
      nav(`/signup?next=/pricing&plan=${plan.id}`);
      return;
    }
    setEasypaisaOpen({ id: plan.id, name: plan.name });
  };

  return (
    <div className="min-h-screen relative pb-24" style={{ background: T.bg, color: T.text, fontFamily: "'Plus Jakarta Sans', sans-serif" }} data-testid="pricing-preview-root">
      <style>{`
        .display { font-family: 'Rajdhani', sans-serif; letter-spacing: -0.01em; }
        .mono    { font-family: 'IBM Plex Mono', monospace; }
      `}</style>
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Rajdhani:wght@500;600;700&family=IBM+Plex+Mono:wght@400;500;600&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap" />
      <AIPropsBackground />

      <header className="relative max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <AuthBrand />
        <Link to="/design-preview/landing" onClick={() => play("hover")}
          className="inline-flex items-center gap-1 mono text-[10px] tracking-[0.3em] hover:underline"
          style={{ color: T.textMuted }}>
          <ChevronLeft className="w-3 h-3" /> BACK TO HOME
        </Link>
      </header>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="text-center max-w-3xl mx-auto">
          <div className="mono text-[10px] tracking-[0.3em] mb-3" style={{ color: T.primary }}>// PRICING · NO HIDDEN FEES</div>
          <h1 className="display text-5xl md:text-6xl font-bold leading-[1.02]">
            Two ways to <span style={{ color: T.primary, textShadow: `0 0 22px ${T.primaryGlow}` }}>train.</span><br />
            One path to your <span style={{ color: T.primary }}>target band</span>.
          </h1>
          <p className="text-lg mt-5 leading-relaxed" style={{ color: T.textMuted }}>
            Start with a free 5-minute AI assessment. Then pick the plan that fits your timeline.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-14">
          {PLANS.map((p) => (
            <HudFrame key={p.id} color={p.featured ? p.accent : T.borderSoft} className={p.featured ? "p-1.5" : "p-1"}>
              <div className="rounded-sm h-full flex flex-col p-7"
                style={{
                  background: p.featured ? `linear-gradient(180deg, ${T.cardBgWarm} 0%, ${T.cardBg} 80%)` : T.cardBg,
                  border: `1px solid ${p.featured ? p.accent : T.borderSoft}`,
                  boxShadow: p.featured ? `0 30px 60px -30px ${T.primaryGlow}` : "0 4px 14px -8px rgba(0,48,73,0.10)",
                }}
                data-testid={`pricing-card-${p.id}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-sm flex items-center justify-center"
                    style={{ background: `${p.accent}15`, color: p.accent, border: `1px solid ${p.accent}40` }}>
                    <p.Icon className="w-6 h-6" />
                  </div>
                  <div className="mono text-[9px] tracking-[0.3em] px-2 py-1 rounded-sm"
                    style={{ background: p.featured ? p.accent : T.bg2, color: p.featured ? T.textOnPrimary : T.textMuted }}>
                    {p.badge}
                  </div>
                </div>
                <div className="display text-3xl font-bold mb-1">{p.name}</div>
                <p className="text-sm mb-5" style={{ color: T.textMuted }}>{p.blurb}</p>

                <div className="mb-6 pb-6 border-b" style={{ borderColor: T.borderSoft }}>
                  <div className="flex items-baseline gap-2">
                    <span className="display font-bold text-5xl" style={{ color: p.accent, textShadow: p.featured ? `0 0 18px ${T.primaryGlow}` : "none" }}>{p.price}</span>
                  </div>
                  <div className="mono text-[10px] tracking-[0.25em] mt-1" style={{ color: T.textMuted }}>{p.cycle}</div>
                </div>

                <ul className="space-y-2.5 mb-6 flex-1">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm">
                      <span className="w-4 h-4 rounded-sm flex items-center justify-center shrink-0 mt-0.5"
                        style={{ background: p.accent, color: "#fff" }}>
                        <Check className="w-2.5 h-2.5" strokeWidth={3} />
                      </span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => { play("primary"); onSubscribe(p); }}
                  disabled={loading === p.id}
                  data-testid={`pricing-cta-${p.id}`}
                  className="w-full h-14 rounded-sm font-bold uppercase tracking-[0.2em] text-sm transition-all duration-300 active:scale-[0.97] inline-flex items-center justify-center gap-2 disabled:opacity-50"
                  style={{
                    background: p.featured ? p.accent : "transparent",
                    color: p.featured ? T.textOnPrimary : p.accent,
                    border: `2px solid ${p.accent}`,
                    boxShadow: p.featured ? `0 0 18px ${T.primaryGlow}, inset 0 0 12px rgba(255,255,255,0.20)` : "none",
                    fontFamily: "'Rajdhani', sans-serif",
                  }}
                  onMouseEnter={(e) => {
                    if (!p.featured) {
                      e.currentTarget.style.background = p.accent;
                      e.currentTarget.style.color = T.textOnPrimary;
                    } else {
                      e.currentTarget.style.boxShadow = `0 0 32px ${T.primaryGlow}, inset 0 0 14px rgba(255,255,255,0.28)`;
                    }
                    e.currentTarget.style.transform = "translateY(-1px)";
                  }}
                  onMouseLeave={(e) => {
                    if (!p.featured) {
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.color = p.accent;
                    } else {
                      e.currentTarget.style.boxShadow = `0 0 18px ${T.primaryGlow}, inset 0 0 12px rgba(255,255,255,0.20)`;
                    }
                    e.currentTarget.style.transform = "";
                  }}
                >
                  {loading === p.id ? "PROCESSING…" : p.cta} <ArrowRight className="w-4 h-4" />
                </button>

                {/* EasyPaisa option — only for PK users on paid plans */}
                {isPK && (p.id === "self_prep_ai" || p.id === "hybrid_live") && (
                  <button onClick={() => { play("click"); onEasypaisa(p); }}
                    data-testid={`easypaisa-btn-${p.id}`}
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 px-5 py-3 font-mono text-xs tracking-[0.25em] uppercase rounded-sm transition-all hover:-translate-y-0.5"
                    style={{ background: "rgba(247,127,0,0.08)", border: `1.5px dashed ${T.primary}`, color: T.primary }}>
                    <Smartphone className="w-4 h-4" /> Pay with EasyPaisa
                  </button>
                )}
              </div>
            </HudFrame>
          ))}
        </div>

        {/* What's covered */}
        <div className="mt-20">
          <div className="text-center mb-8">
            <div className="mono text-[10px] tracking-[0.3em] mb-2" style={{ color: T.primary }}>// EVERY PLAN COVERS</div>
            <h2 className="display text-3xl md:text-4xl font-bold">All 4 modules. All 4 exams.</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { Icon: Headphones, l: "Listening" },
              { Icon: BookOpen,   l: "Reading" },
              { Icon: Pencil,     l: "Writing" },
              { Icon: Mic,        l: "Speaking" },
            ].map((m, i) => (
              <HudFrame key={i} className="p-1" color={T.borderSoft}>
                <div className="rounded-sm p-5 text-center" style={{ background: T.cardBg, border: `1px solid ${T.borderSoft}` }}>
                  <m.Icon className="w-6 h-6 mx-auto" style={{ color: T.primary }} />
                  <div className="display text-xl font-bold mt-2">{m.l}</div>
                </div>
              </HudFrame>
            ))}
          </div>
        </div>

        {/* FAQ-lite */}
        <div className="mt-16 grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
          {[
            { q: "Can I switch plans later?", a: "Yes. Upgrade from Self Prep to Hybrid anytime — we credit your unused month toward the course." },
            { q: "What if my band doesn't improve?", a: "Hybrid candidates get a full money-back guarantee if they don't see at least a 0.5 band improvement after the course." },
            { q: "Do you accept other payment methods?", a: "Stripe (card, Apple Pay, Google Pay), PayPal and bank transfer are supported. Local methods may vary by country." },
            { q: "Is there a student discount?", a: "Yes — verified students get 20% off Self Prep AI. Email education@englishtest.uk with your student ID." },
          ].map((f, i) => (
            <div key={i} className="rounded-sm p-5" style={{ background: T.cardBg, border: `1px solid ${T.borderSoft}` }}>
              <div className="display text-lg font-semibold mb-1.5">{f.q}</div>
              <div className="text-sm" style={{ color: T.textMuted }}>{f.a}</div>
            </div>
          ))}
        </div>
      </div>

      <EasyPaisaCheckout
        open={!!easypaisaOpen}
        planId={easypaisaOpen?.id}
        planName={easypaisaOpen?.name}
        onClose={() => setEasypaisaOpen(null)}
        onSubmitted={() => { /* keep modal open showing done state — user closes */ }}
      />
    </div>
  );
}
